import brand from '@helpers/brand';
import React from 'react';
import { Controller, useForm, } from 'react-hook-form';
import PuffLoader from 'react-spinners/PuffLoader';
import AttributerTrackingField from './FormControls/AttributerTrackingField/AttributerTrackingField';
import CheckBox from './FormControls/CheckBox/CheckBox';
import CheckBoxList from './FormControls/CheckBoxList/CheckBoxList';
import Date from './FormControls/Date/Date';
import DropDownList from './FormControls/DropDownList/DropDownList';
import FileUpload from './FormControls/FileUpload/FileUpload';
import HiddenField from './FormControls/Hiddenfield/Hiddenfield';
import MultipleFileUpload from './FormControls/MultipleFileUpload/MultipleFileUpload';
import Password from './FormControls/Password/Password';
import RadioButtonList from './FormControls/RadioButtonList/RadioButtonList';
import Recaptcha2 from './FormControls/Recaptcha2/Recaptcha2';
import RichText from './FormControls/RichText/RichText';
import Textarea from './FormControls/Textarea/Textarea';
import Textfield from './FormControls/Textfield/Textfield';
import TitleAndDescription from './FormControls/TitleAndDescription/TitleAndDescription';
import S from './UmbracoForm.styles';
import { FormConditionsAction, FormConditionsLogic, FormRuleOperation, } from './form';
import Button from '../Global/Buttons/Button/Button';
const UmbracoForm = ({ id, formModel, formInstanceId, reCaptchaSiteKey, submitText = 'Submit', submitUrl, onSubmitError, onSubmitSuccess, }) => {
    const refContainer = React.useRef(null);
    const { control, formState: { dirtyFields, errors, isSubmitted, isSubmitting }, handleSubmit, register, watch, } = useForm();
    const values = watch();
    const [confirmationMessage, setConfirmationMessage] = React.useState('');
    const [serverErrors, setServerErrors] = React.useState([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    React.useEffect(() => {
        if (Object.keys(errors).length === 0) {
            return;
        }
        // TODO: Scroll to first error.
        if (refContainer.current) {
            refContainer.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentPage, errors]);
    return (React.createElement(S.Container, { ref: refContainer, "data-submitted": isSubmitted },
        confirmationMessage && React.createElement(S.ConfirmationMessage, null, confirmationMessage),
        !confirmationMessage && (React.createElement(S.Form, { noValidate: true, onSubmit: handleSubmit(onSubmitValid), id: formModel.id },
            serverErrors.length > 0 && (React.createElement(S.ErrorList, null, serverErrors.map((error, index) => (React.createElement(S.ErrorListItem, { key: `error-${index}` }, error))))),
            React.createElement("input", { type: "hidden", value: formModel.id, ...register('formId', { required: true }) }),
            formModel.pages.map((page, pi) => (React.createElement(React.Fragment, { key: `page_${pi}` }, renderPage(page, pi, formInstanceId)))),
            React.createElement(S.ButtonWrapper, null,
                currentPage >= 1 && formModel.pages.length > 1 && (React.createElement(S.Previous, null,
                    React.createElement(Button, { buttonType: 'outline', disabled: isSubmitting, title: formModel.previousLabel ?? 'Previous', onClick: () => setCurrentPage(currentPage - 1), type: "button", iconDirection: "left" }))),
                currentPage === formModel.pages.length - 1 && (React.createElement(S.Submit, null,
                    React.createElement(Button, { buttonType: "default", disabled: isSubmitting, title: submitText ?? formModel.submitLabel, type: "submit", icon: isSubmitting ? React.createElement(PuffLoader, { color: "currentColor", size: 17 }) : undefined }, submitText ?? formModel.submitLabel),
                    isSubmitting && (React.createElement(S.SubmitIndicator, null,
                        React.createElement(PuffLoader, { color: brand.global.white, size: 32 }))))),
                currentPage < formModel.pages.length - 1 && (React.createElement(S.Next, null,
                    React.createElement(Button, { buttonType: 'default', disabled: isSubmitting, title: formModel.nextLabel ?? 'Next', onClick: () => setCurrentPage(currentPage + 1), type: "button" }, formModel.nextLabel ?? 'Next'))))))));
    function renderPage(page, pi, formInstanceId) {
        const hasRules = page.condition?.rules?.length;
        if (page.condition !== null && hasRules) {
            if ((page.condition?.actionType === FormConditionsAction.Show &&
                !rulesMatch(page.condition, values)) || // If we are in show mode and the rules don't match
                (page.condition?.actionType === FormConditionsAction.Hide &&
                    rulesMatch(page.condition, values))) {
                // If we are in hide mode and the rules match
                return React.createElement(React.Fragment, null);
            }
        }
        return (React.createElement(S.Fieldset, { "aria-current": pi === currentPage },
            page.caption && React.createElement(S.Legend, null, page.caption),
            page.fieldsets.map((fieldSet, fsi) => renderFieldset(fieldSet, pi, fsi, formInstanceId))));
    }
    function renderFieldset(fieldSet, pi, fsi, formInstanceId) {
        const hasRules = fieldSet.condition?.rules?.length;
        if (fieldSet.condition !== null && hasRules) {
            if ((fieldSet.condition?.actionType === FormConditionsAction.Show &&
                !rulesMatch(fieldSet.condition, values)) || // If we are in show mode and the rules don't match
                (fieldSet.condition?.actionType === FormConditionsAction.Hide &&
                    rulesMatch(fieldSet.condition, values))) {
                // If we are in hide mode and the rules match
                return React.createElement(React.Fragment, null);
            }
        }
        return (React.createElement(S.FieldGroup, { key: fieldSet.id }, fieldSet.columns.map((column, ci) => (React.createElement(S.FieldRow, { key: `${pi}_${fsi}_${ci}`, "data-columns": column.width }, column.fields.map((field, fi) => (React.createElement(React.Fragment, { key: `${pi}_${fsi}_${ci}_${fi}` }, renderField(field, formInstanceId, errors[field.alias], dirtyFields[field.alias])))))))));
    }
    function renderField(fieldModel, formInstanceId, error, dirty = false) {
        const registerOptions = {
            pattern: fieldModel.pattern
                ? {
                    message: fieldModel.patternInvalidErrorMessage
                        ? fieldModel.patternInvalidErrorMessage
                        : 'Please enter a valid value.',
                    value: new RegExp(fieldModel.pattern),
                }
                : undefined,
            required: {
                message: fieldModel.requiredErrorMessage
                    ? fieldModel.requiredErrorMessage
                    : 'Please enter a value.',
                value: fieldModel.required,
            },
        };
        const hasRules = fieldModel.condition?.rules?.length;
        if (fieldModel.condition !== null && hasRules) {
            if ((fieldModel.condition?.actionType === FormConditionsAction.Show &&
                !rulesMatch(fieldModel.condition, values)) || // If we are in show mode and the rules don't match
                (fieldModel.condition?.actionType === FormConditionsAction.Hide &&
                    rulesMatch(fieldModel.condition, values))) {
                // If we are in hide mode and the rules match
                return React.createElement(React.Fragment, null);
            }
        }
        switch (fieldModel.type.name) {
            case 'Attributer Tracking Field':
                return (React.createElement(AttributerTrackingField, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Checkbox':
            case 'Data Consent':
                return (React.createElement(CheckBox, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'CheckBoxList':
            case 'Multiple choice':
                return (React.createElement(CheckBoxList, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Date':
                return (React.createElement(Date, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Dropdown':
            case 'DropDownList':
                return (React.createElement(DropDownList, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'File upload':
                return fieldModel.fileUploadOptions?.allowMultipleFileUploads ? (React.createElement(MultipleFileUpload, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) })) : (React.createElement(FileUpload, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Hidden':
                return (React.createElement(HiddenField, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Password':
                return (React.createElement(Password, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'RadioButtonList':
            case 'Single choice':
                return (React.createElement(RadioButtonList, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Recaptcha2':
                return (React.createElement(Controller, { control: control, defaultValue: "", name: fieldModel.alias, rules: registerOptions, render: ({ field }) => (React.createElement(Recaptcha2, { dirty: dirty, error: errors[fieldModel.alias], field: fieldModel, siteKey: reCaptchaSiteKey, ...field })) }));
            case 'reCAPTCHA v3 with score':
                return null;
            // return (
            //   <Controller
            //     control={control}
            //     defaultValue=""
            //     name={fieldModel.alias}
            //     rules={registerOptions}
            //     render={({ field }) => (
            //       <Recaptcha3
            //         dirty={dirty}
            //         error={errors[fieldModel.alias]}
            //         field={fieldModel}
            //         siteKey={reCaptchaSiteKey}
            //         {...field}
            //       />
            //     )}
            //   />
            // );
            case 'Rich text':
                return React.createElement(RichText, { field: fieldModel });
            case 'Textarea':
            case 'Long answer':
                return (React.createElement(Textarea, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Textfield':
            case 'Short answer':
                return (React.createElement(Textfield, { id: `${formInstanceId}-${id}-${fieldModel.alias}`, dirty: dirty, error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'TitleAndDescription':
            case 'Title and description':
                return React.createElement(TitleAndDescription, { field: fieldModel });
            default:
                throw new Error(`Form Error: Unsupported field type ${fieldModel.type}.`);
        }
    }
    async function onSubmitValid(formState, event) {
        return postFormData(formState, event);
    }
    async function postFormData(formState, event) {
        const formData = new FormData(event?.target);
        const res = await fetch(submitUrl, {
            body: formData,
            method: 'POST',
        });
        const resData = (await res.json());
        if (resData.errors?.length) {
            setServerErrors(resData.errors);
            if (onSubmitError) {
                onSubmitError();
            }
        }
        // Execute event tracking.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'formSubmit',
            formId: formModel.id,
            formName: formModel.name,
            pageUrl: window.location.href,
        });
        if (resData.message) {
            setConfirmationMessage(resData.message);
            if (onSubmitSuccess) {
                onSubmitSuccess();
            }
        }
        if (resData.redirect) {
            window.location.href = resData.redirect;
        }
    }
};
function rulesMatch(condition, values) {
    const matches = condition?.rules?.map((rule) => {
        const stateValue = values[rule.field]?.toString().toUpperCase();
        const rulesValue = rule.value?.toString().toUpperCase();
        switch (rule.operator) {
            case FormRuleOperation.Is:
                return stateValue === rulesValue;
            case FormRuleOperation.IsNot:
                return stateValue !== rulesValue;
            case FormRuleOperation.IsGreaterThan:
                return parseInt(stateValue) > parseInt(rule.value);
            case FormRuleOperation.IsLessThan:
                return parseInt(stateValue) < parseInt(rule.value);
            case FormRuleOperation.Contains:
                return stateValue && stateValue.includes(rulesValue);
            case FormRuleOperation.StartsWith:
                return stateValue && stateValue.startsWith(rulesValue);
            case FormRuleOperation.EndsWith:
                return stateValue && stateValue.endsWith(rulesValue);
            default:
                return false;
        }
    });
    if (condition?.logicType === FormConditionsLogic.All) {
        return !!matches && matches.filter((match) => !match).length === 0;
    }
    else {
        return !!matches && matches.filter((match) => match).length > 0;
    }
}
export default UmbracoForm;
